import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, Plus, Pencil, Trash2, Eye, Phone, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const emptyForm = {
  client_name: '', pic_name: '', phone_number: '', whatsapp_number: '',
  email: '', address: '', company_name: '', notes: '',
};

export default function Clients() {
  const { role } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clients', { params: search ? { search } : {} });
      setList(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...c });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) { toast.error('Nama client wajib diisi'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success('Client diperbarui');
      } else {
        await api.post('/clients', form);
        toast.success('Client ditambahkan');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success('Client dihapus');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const canEdit = PERMISSIONS.canEdit(role);
  const canDelete = PERMISSIONS.canDeleteFinancial(role);

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="clients-search-input"
              placeholder="Cari client, PIC, perusahaan…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="clients-add-button">
            <Plus className="h-4 w-4 mr-2" /> Tambah Client
          </Button>
        )}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="clients-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead>Nama</TableHead>
                  <TableHead>PIC</TableHead>
                  <TableHead>Perusahaan</TableHead>
                  <TableHead>Kontak</TableHead>
                  <TableHead className="w-[140px] text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…</TableCell></TableRow>
                )}
                {!loading && list.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Belum ada client.</TableCell></TableRow>
                )}
                {list.map((c) => (
                  <TableRow key={c.id} className="hover:bg-white/4 border-border" data-testid={`clients-row-${c.id}`}>
                    <TableCell className="font-medium cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>{c.client_name}</TableCell>
                    <TableCell className="text-muted-foreground">{c.pic_name || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{c.company_name || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5 text-xs">
                        {c.phone_number ? <span className="flex items-center gap-1.5 text-muted-foreground"><Phone className="h-3 w-3" />{c.phone_number}</span> : null}
                        {c.email ? <span className="flex items-center gap-1.5 text-muted-foreground"><Mail className="h-3 w-3" />{c.email}</span> : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`clients-view-${c.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canEdit && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`clients-edit-${c.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-300" data-testid={`clients-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus client?</AlertDialogTitle>
                              <AlertDialogDescription>Aksi ini tidak dapat dibatalkan. Client dengan invoice tidak bisa dihapus.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(c)} data-testid={`clients-delete-confirm-${c.id}`}>Hapus</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Client' : 'Tambah Client Baru'}</DialogTitle>
            <DialogDescription>Isi informasi client dengan lengkap.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Nama Client *</Label>
              <Input data-testid="client-form-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nama PIC</Label>
              <Input data-testid="client-form-pic" value={form.pic_name} onChange={(e) => setForm({ ...form, pic_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Perusahaan</Label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Telepon</Label>
              <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">WhatsApp</Label>
              <Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Alamat</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="client-form-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
